// src/modules/requerimiento/components/RequerimientoReporte.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  Button,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Typography,
  TablePagination,
  Alert,
  Chip,
  Tooltip,
  Checkbox,
  FormControlLabel,
  IconButton,
  useTheme,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import VisibilityIcon from "@mui/icons-material/Visibility";

import { getReqSolicitudes, exportarReqSolicitudesExcel, exportarReqSolicitudesPdf } from "../services/reqSolicitudes";
import { usePlataformasYTecnicos } from "../hooks/usePlataformasYTecnicos";

const TIPOS_REQ = [
  { value: "", label: "Todos" },
  { value: "3", label: "Adición" },
  { value: "2", label: "Mantenimiento" },
  { value: "5", label: "Devolución" },
];

const ESTADOS = [
  { value: "", label: "Todos" },
  { value: "7", label: "Revisión en Sistemas" },
  { value: "1", label: "Coordinación" },
  { value: "2", label: "Programación" },
  { value: "3", label: "Trabajo de campo" },
  { value: "8", label: "Finalizado" },
  { value: "0", label: "Eliminado" },
];

const ESTADO_CHIP = {
  0: { label: "Eliminado", color: "default" },
  1: { label: "Coordinación", color: "warning" },
  2: { label: "Programación", color: "info" },
  3: { label: "Trabajo de campo", color: "primary" },
  4: { label: "Trabajo de campo", color: "primary" },
  7: { label: "Revisión en Sistemas", color: "secondary" },
  8: { label: "Finalizado", color: "success" },
};

const compactFieldSx = {
  "& .MuiInputBase-root": { height: 34, fontSize: "0.82rem" },
  "& .MuiInputLabel-root": { fontSize: "0.82rem" },
  "& .MuiButton-root": { minHeight: 34 },
  "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
};

const fmt = (v) => {
  if (!v) return "-";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("es-BO", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const downloadBlob = (data, contentType, filename) => {
  const url = URL.createObjectURL(new Blob([data], { type: contentType }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

export default function RequerimientoReporte({ onOpenDetalle }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { plataformas, fetchPlataformas } = usePlataformasYTecnicos();

  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [tipoReq, setTipoReq] = useState("");
  const [estado, setEstado] = useState("");
  const [cliente, setCliente] = useState("");
  const [tecnico, setTecnico] = useState("");
  const [plataforma, setPlataforma] = useState("");
  const [soloTercializados, setSoloTercializados] = useState(false);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [exportando, setExportando] = useState("");

  useEffect(() => {
    fetchPlataformas();
  }, [fetchPlataformas]);

  const buildParams = (overrides = {}) => ({
    fechaInicio: fechaInicio || undefined,
    fechaFin: fechaFin || undefined,
    tipo_req: tipoReq || undefined,
    estado: estado || undefined,
    buscar: cliente.trim() || undefined,
    tecnicos: tecnico.trim() || undefined,
    plataforma: plataforma || undefined,
    soloTercializados: (overrides.soloTercializados ?? soloTercializados) ? "true" : undefined,
    pendiente: "false",
  });

  const fetchReporte = async ({ pageArg = page, pageSizeArg = rowsPerPage, soloTercializadosArg } = {}) => {
    setLoading(true);
    setError("");
    try {
      const params = { ...buildParams({ soloTercializados: soloTercializadosArg }), page: pageArg + 1, pageSize: pageSizeArg };
      const { data } = await getReqSolicitudes(params);
      setRows(Array.isArray(data?.rows) ? data.rows : []);
      setTotal(Number(data?.total) || 0);
      setPage(pageArg);
      setRowsPerPage(pageSizeArg);
    } catch (e) {
      setError("No se pudo cargar el reporte de requerimientos.");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReporte({ pageArg: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleExportar = async (tipo) => {
    setExportando(tipo);
    setError("");
    try {
      const params = buildParams();
      if (tipo === "excel") {
        const res = await exportarReqSolicitudesExcel(params);
        downloadBlob(
          res.data,
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          "reporte_requerimientos.xlsx"
        );
      } else {
        const res = await exportarReqSolicitudesPdf(params);
        downloadBlob(res.data, "application/pdf", "reporte_requerimientos.pdf");
      }
    } catch (e) {
      setError("No se pudo exportar el reporte.");
    } finally {
      setExportando("");
    }
  };

  return (
    <Box>
      <Grid container spacing={1} alignItems="center" sx={{ mb: 1.25, ...compactFieldSx }}>
        <Grid size={{ xs: 6, sm: 3, md: 1.5 }}>
          <TextField
            type="date" size="small" label="Desde" fullWidth
            InputLabelProps={{ shrink: true }}
            value={fechaInicio}
            onChange={(e) => setFechaInicio(e.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3, md: 1.5 }}>
          <TextField
            type="date" size="small" label="Hasta" fullWidth
            InputLabelProps={{ shrink: true }}
            value={fechaFin}
            onChange={(e) => setFechaFin(e.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3, md: 1.5 }}>
          <TextField select size="small" label="Tipo" fullWidth value={tipoReq} onChange={(e) => setTipoReq(e.target.value)}>
            {TIPOS_REQ.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid size={{ xs: 6, sm: 3, md: 1.7 }}>
          <TextField select size="small" label="Estado" fullWidth value={estado} onChange={(e) => setEstado(e.target.value)}>
            {ESTADOS.map((e) => <MenuItem key={e.value} value={e.value}>{e.label}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid size={{ xs: 6, sm: 3, md: 1.7 }}>
          <TextField select size="small" label="Plataforma" fullWidth value={plataforma} onChange={(e) => setPlataforma(e.target.value)}>
            <MenuItem value="">Todas</MenuItem>
            {plataformas.map((p) => <MenuItem key={p.id} value={p.id}>{p.nombre}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <TextField size="small" label="Cliente" fullWidth value={cliente} onChange={(e) => setCliente(e.target.value)} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 1.6 }}>
          <TextField size="small" label="Técnico" fullWidth value={tecnico} onChange={(e) => setTecnico(e.target.value)} />
        </Grid>
        <Grid size={{ xs: 12, md: 1 }}>
          <Button variant="contained" fullWidth disabled={loading} onClick={() => fetchReporte({ pageArg: 0 })}>
            {loading ? "..." : "Filtrar"}
          </Button>
        </Grid>
      </Grid>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.25, flexWrap: "wrap" }}>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={soloTercializados}
              onChange={(e) => {
                setSoloTercializados(e.target.checked);
                fetchReporte({ pageArg: 0, soloTercializadosArg: e.target.checked });
              }}
            />
          }
          label={<Typography variant="body2" sx={{ fontSize: "0.8rem" }}>Solo con cliente tercializado</Typography>}
          sx={{ mr: 0 }}
        />
        <Typography variant="caption" color="text.secondary">Total: {total}</Typography>
        <Box sx={{ flex: 1 }} />
        <Button
          size="small" variant="outlined" color="success"
          startIcon={exportando === "excel" ? <CircularProgress size={14} color="inherit" /> : <FileDownloadIcon />}
          disabled={!!exportando || total === 0}
          onClick={() => handleExportar("excel")}
        >
          {exportando === "excel" ? "Exportando..." : "Exportar Excel"}
        </Button>
        <Button
          size="small" variant="outlined" color="error"
          startIcon={exportando === "pdf" ? <CircularProgress size={14} color="inherit" /> : <PictureAsPdfIcon />}
          disabled={!!exportando || total === 0}
          onClick={() => handleExportar("pdf")}
        >
          {exportando === "pdf" ? "Exportando..." : "Exportar PDF"}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

      {!loading && rows.length === 0 ? (
        <Alert severity="info">No hay requerimientos para los filtros seleccionados.</Alert>
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined" sx={{ overflowX: "auto" }}>
            <Table
              size="small"
              sx={{
                "& .MuiTableCell-root": { px: 0.65, py: 0.4, fontSize: "0.76rem", lineHeight: 1.2 },
                "& .MuiTableCell-head": {
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  color: theme.palette.text.primary,
                  backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[800] : theme.palette.grey[100],
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Servicio</TableCell>
                  <TableCell>Sucursal</TableCell>
                  <TableCell>Placa(s)</TableCell>
                  <TableCell>Técnico(s)</TableCell>
                  <TableCell>Fecha Coord.</TableCell>
                  <TableCell align="center">Estado</TableCell>
                  <TableCell>Cliente Tercializado</TableCell>
                  <TableCell>Emitido</TableCell>
                  <TableCell>Registro</TableCell>
                  <TableCell align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={13} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((r) => {
                    const chip = ESTADO_CHIP[Number(r.estado)] || { label: `Estado ${r.estado}`, color: "default" };
                    return (
                      <TableRow key={r.cod} hover>
                        <TableCell>{r.cod}</TableCell>
                        <TableCell>{r.nombre_ot_tipo || "-"}</TableCell>
                        <TableCell>
                          <Tooltip title={r.razon_social || "-"} arrow>
                            <Box component="span" sx={{ display: "block", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {r.razon_social || "-"}
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell>{r.descripcion || "-"}</TableCell>
                        <TableCell>{r.sucursal || "-"}</TableCell>
                        <TableCell>{r.placa || "-"}</TableCell>
                        <TableCell>{r.tecnico || "-"}</TableCell>
                        <TableCell>{fmt(r.fecha_coordinacion)} {r.hora_coordinacion || ""}</TableCell>
                        <TableCell align="center">
                          <Chip label={chip.label} color={chip.color} size="small" sx={{ height: 19, fontSize: "0.68rem", "& .MuiChip-label": { px: 0.75 } }} />
                        </TableCell>
                        <TableCell>
                          {r.razon_social_tercia
                            ? <Chip label={r.razon_social_tercia} size="small" color="info" variant="outlined" sx={{ height: 19, fontSize: "0.68rem", "& .MuiChip-label": { px: 0.75 } }} />
                            : "-"}
                        </TableCell>
                        <TableCell>{r.usu || "-"}</TableCell>
                        <TableCell>{fmt(r.fecha)}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="Ver detalle">
                            <IconButton
                              size="small"
                              sx={{ p: 0.35 }}
                              onClick={() => (onOpenDetalle ? onOpenDetalle(r) : navigate(`/requerimiento/${r.cod}`))}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={total}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[50, 100, 200]}
            labelRowsPerPage="Filas por página"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
            onPageChange={(_, newPage) => fetchReporte({ pageArg: newPage })}
            onRowsPerPageChange={(e) => fetchReporte({ pageArg: 0, pageSizeArg: parseInt(e.target.value, 10) || 50 })}
            sx={{
              minHeight: 40,
              "& .MuiTablePagination-toolbar": { minHeight: 40, px: 1 },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { fontSize: "0.78rem" },
            }}
          />
        </>
      )}
    </Box>
  );
}
